import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import SkinAssistantNav from "../../components/admin/SkinAssistantNav";
import ProductIdMultiPicker from "../../components/admin/ProductIdMultiPicker";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import { getAllProducts } from "../../helper/product_helper";
import {
  API_NOT_DEPLOYED_MSG,
  AGE_BAND_OPTIONS,
  BUDGET_OPTIONS,
  CONCERN_OPTIONS,
  SENSITIVITY_OPTIONS,
  SKIN_TYPE_OPTIONS,
  USES_SPF_OPTIONS,
  buildProductOptions,
  buildRulesPutPayload,
  createEmptyRule,
  getSkinAssistantRules,
  isSkinAssistantApiNotDeployed,
  parseRulesResponse,
  putSkinAssistantRules,
  validateRulesSet,
} from "../../helper/skin_assistant_helper";
import { getAdminToken } from "../../helper/auth_helper";

function RulesEditor() {
  const token = getAdminToken();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [rules, setRules] = useState([]);
  const [version, setVersion] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [expandedRuleId, setExpandedRuleId] = useState(null);
  const [showJson, setShowJson] = useState(false);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const productOptions = useMemo(() => buildProductOptions(products), [products]);
  const knownProductIds = useMemo(
    () => new Set(productOptions.map((opt) => opt.value)),
    [productOptions]
  );

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const res = await getAllProducts({ page: 1, limit: 500 });
      let list = [];
      if (res?.success && Array.isArray(res?.data?.products)) list = res.data.products;
      else if (Array.isArray(res?.products)) list = res.products;
      else if (Array.isArray(res?.data)) list = res.data;
      setProducts(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load products for picker");
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const loadRules = useCallback(async () => {
    if (!token) {
      setLoadError("Not authorized");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    setApiUnavailable(false);
    try {
      const res = await getSkinAssistantRules(token);
      const parsed = parseRulesResponse(res);
      if (parsed.error) {
        setLoadError(parsed.error);
        setRules([]);
      } else {
        setRules(parsed.rules.length ? parsed.rules : [createEmptyRule()]);
        setVersion(parsed.version);
        setUpdatedAt(parsed.updatedAt);
        setUpdatedBy(parsed.updatedBy);
        if (parsed.rules[0]?.id) setExpandedRuleId(parsed.rules[0].id);
      }
    } catch (error) {
      if (isSkinAssistantApiNotDeployed(error)) {
        setApiUnavailable(true);
        setRules([createEmptyRule()]);
      } else {
        setLoadError(error?.message || "Failed to load rules");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadProducts();
    loadRules();
  }, [loadProducts, loadRules]);

  const updateRule = (ruleId, patch) => {
    setRules((prev) =>
      prev.map((rule) => (rule.id === ruleId ? { ...rule, ...patch } : rule))
    );
  };

  const updateRuleMatch = (ruleId, field, value) => {
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? { ...rule, match: { ...rule.match, [field]: value } }
          : rule
      )
    );
  };

  const updateRuleRoutine = (ruleId, period, productIds) => {
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? { ...rule, routine: { ...rule.routine, [period]: productIds } }
          : rule
      )
    );
  };

  const addRule = () => {
    const next = createEmptyRule(rules.length);
    setRules((prev) => [...prev, next]);
    setExpandedRuleId(next.id);
  };

  const removeRule = (ruleId) => {
    setRules((prev) => {
      const next = prev.filter((rule) => rule.id !== ruleId);
      return next.length ? next : [createEmptyRule()];
    });
  };

  const handleSave = async () => {
    const errors = validateRulesSet(rules, knownProductIds);
    setValidationErrors(errors);
    if (errors.length) {
      toast.error("Fix validation errors before saving");
      return;
    }

    setSaving(true);
    try {
      const payload = buildRulesPutPayload(rules, version);
      const res = await putSkinAssistantRules(payload, token);
      const parsed = parseRulesResponse(res);
      if (parsed.error) {
        toast.error(parsed.error);
        return;
      }
      setRules(parsed.rules.length ? parsed.rules : rules);
      setVersion(parsed.version ?? version);
      setUpdatedAt(parsed.updatedAt ?? new Date().toISOString());
      setUpdatedBy(parsed.updatedBy);
      setApiUnavailable(false);
      toast.success("Skin Assistant rules saved");
    } catch (error) {
      if (isSkinAssistantApiNotDeployed(error)) {
        setApiUnavailable(true);
        toast.error(API_NOT_DEPLOYED_MSG);
      } else {
        toast.error(error?.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const jsonPreview = useMemo(
    () => JSON.stringify(buildRulesPutPayload(rules, version), null, 2),
    [rules, version]
  );

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">
                <Link to="/admin/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Skin Assistant
              </li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Skin Assistant — Rules</h4>
            <p className="text-secondary small mb-0 mt-1">
              Map quiz answers to product recommendations and AM/PM routines
            </p>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button variant="outline-secondary" size="sm" onClick={loadRules} disabled={loading}>
              Refresh
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || loading}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Saving…
                </>
              ) : (
                "Save rules"
              )}
            </Button>
          </div>
        </div>

        <SkinAssistantNav />

        {apiUnavailable && (
          <Alert variant="warning" className="mb-3">
            {API_NOT_DEPLOYED_MSG} You can still draft rules locally; save will work once the API is
            deployed.
          </Alert>
        )}

        {loadError && !apiUnavailable && (
          <Alert variant="danger" className="mb-3">{loadError}</Alert>
        )}

        {validationErrors.length > 0 && (
          <Alert variant="danger" className="mb-3">
            <ul className="mb-0 ps-3">
              {validationErrors.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Card className="mb-3">
          <Card.Body className="py-3">
            <Row className="g-2 small">
              <Col md={4}>
                <span className="text-secondary">Version:</span>{" "}
                <strong>{version ?? "—"}</strong>
              </Col>
              <Col md={4}>
                <span className="text-secondary">Last updated:</span>{" "}
                <strong>
                  {updatedAt ? moment(updatedAt).format("DD MMM YYYY, hh:mm A") : "—"}
                </strong>
              </Col>
              <Col md={4}>
                <span className="text-secondary">Updated by:</span>{" "}
                <strong>{updatedBy || "—"}</strong>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Recommendation rules ({rules.length})</h6>
              <Button variant="outline-primary" size="sm" onClick={addRule}>
                Add rule
              </Button>
            </div>

            {rules.length === 0 ? (
              <AdminEmptyState
                title="No rules yet"
                description="Add a rule to map quiz answers to products."
                actionLabel="Add rule"
                onAction={addRule}
              />
            ) : (
              rules.map((rule, index) => {
                const open = expandedRuleId === rule.id;
                return (
                  <Card key={rule.id} className="mb-3">
                    <Card.Header
                      className="d-flex justify-content-between align-items-center py-2"
                      onClick={() => setExpandedRuleId(open ? null : rule.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <span className="fw-semibold small">
                        Rule {index + 1}
                        {rule.match?.concern || rule.match?.skinType
                          ? ` — ${rule.match.concern || "any concern"} / ${rule.match.skinType || "any type"}`
                          : ""}
                      </span>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRule(rule.id);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card.Header>
                    <Collapse in={open}>
                      <div>
                        <Card.Body>
                          <Row className="g-3">
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="small">Skin type</Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={rule.match.skinType}
                                  onChange={(e) =>
                                    updateRuleMatch(rule.id, "skinType", e.target.value)
                                  }
                                >
                                  <option value="">Any</option>
                                  {SKIN_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="small">Primary concern</Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={rule.match.concern}
                                  onChange={(e) =>
                                    updateRuleMatch(rule.id, "concern", e.target.value)
                                  }
                                >
                                  <option value="">Any</option>
                                  {CONCERN_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="small">Budget</Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={rule.match.budget}
                                  onChange={(e) =>
                                    updateRuleMatch(rule.id, "budget", e.target.value)
                                  }
                                >
                                  <option value="">Any</option>
                                  {BUDGET_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="small">Age band</Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={rule.match.ageBand}
                                  onChange={(e) =>
                                    updateRuleMatch(rule.id, "ageBand", e.target.value)
                                  }
                                >
                                  <option value="">Any</option>
                                  {AGE_BAND_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="small">Sensitivity</Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={rule.match.sensitivity}
                                  onChange={(e) =>
                                    updateRuleMatch(rule.id, "sensitivity", e.target.value)
                                  }
                                >
                                  <option value="">Any</option>
                                  {SENSITIVITY_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="small">Uses SPF</Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={rule.match.usesSpf}
                                  onChange={(e) =>
                                    updateRuleMatch(rule.id, "usesSpf", e.target.value)
                                  }
                                >
                                  {USES_SPF_OPTIONS.map((opt) => (
                                    <option key={opt.label} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="g-3 mt-2">
                            <Col lg={4}>
                              <ProductIdMultiPicker
                                label="Recommended products"
                                options={productOptions}
                                value={rule.recommendedProductIds}
                                onChange={(ids) =>
                                  updateRule(rule.id, { recommendedProductIds: ids })
                                }
                                disabled={productsLoading}
                                helpText="Shown as the main product bundle for this answer set."
                              />
                            </Col>
                            <Col lg={4}>
                              <ProductIdMultiPicker
                                label="AM routine"
                                options={productOptions}
                                value={rule.routine?.am || []}
                                onChange={(ids) => updateRuleRoutine(rule.id, "am", ids)}
                                disabled={productsLoading}
                              />
                            </Col>
                            <Col lg={4}>
                              <ProductIdMultiPicker
                                label="PM routine"
                                options={productOptions}
                                value={rule.routine?.pm || []}
                                onChange={(ids) => updateRuleRoutine(rule.id, "pm", ids)}
                                disabled={productsLoading}
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </div>
                    </Collapse>
                  </Card>
                );
              })
            )}

            <Card className="mt-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span className="small fw-semibold">JSON preview (read-only)</span>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setShowJson((v) => !v)}
                >
                  {showJson ? "Hide" : "Show"}
                </Button>
              </Card.Header>
              {showJson && (
                <Card.Body>
                  <pre
                    className="small bg-light p-3 rounded mb-0"
                    style={{ maxHeight: 320, overflow: "auto" }}
                  >
                    {jsonPreview}
                  </pre>
                </Card.Body>
              )}
            </Card>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}

export default RulesEditor;
